import type { PaginationEventHandlersParams } from '@ksyos/design-system/dist/components/pagination';
import { Button, PageHeader, Pagination } from '@ksyos/design-system';
import { Link as ReactRouterLink } from '@ksyos/router';
import { useCallback, useEffect, useState } from 'react';
import { ManagerAuthorization, ValidationErrorCode } from '@common/enums';
import { formatString } from '@common/utils/formatString';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils';
import { assertHasAuthorization } from '@frontend/utils/assertHasAuthorization';
import type { IInsurerServiceDeclarationRule } from '../../../interfaces/insurerServiceDeclarationRule';
import { DangerAlertWhenErrorCode, Loading } from '../../../elements/common';
import { InsurerServiceDeclarationRulesListTable } from './InsurerServiceDeclarationRulesListTable';

type InsurerServiceDeclarationRulesListProps = {
    serviceId: string;
};

export function InsurerServiceDeclarationRulesList({ serviceId }: InsurerServiceDeclarationRulesListProps) {
    assertHasAuthorization(ManagerAuthorization.servicesRead);
    const { serviceDeclarationRules, common } = getSelectedLanguage();
    const abortableFetch = useAbortableFetch();

    const [canAdd, setCanAdd] = useState(false);
    const [errorCode, setErrorCode] = useState<string>();
    const [isSearching, setIsSearching] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [insurerServiceDeclarationRules, setInsurerServiceDeclarationRules] = useState<
        IInsurerServiceDeclarationRule[]
    >([]);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        search(page);
    }, []);

    const handlePaginationChange = useCallback(
        async ({ page: newPage, pageSize: newPageSize }: PaginationEventHandlersParams) => {
            setPageSize(newPageSize);
            setPage(newPage);
            await search(newPage, newPageSize);
        },
        []
    );

    const actionButton = canAdd && (
        <Button
            as={ReactRouterLink}
            color="secondary"
            iconLeft="add"
            to={`/insurerServiceDeclarationRules/add?serviceId=${serviceId}`}
        >
            {formatString(common.addItem, [serviceDeclarationRules.insurerServiceDeclarationRule])}
        </Button>
    );

    return (
        <>
            <PageHeader
                action={actionButton}
                title={serviceDeclarationRules.insurerServiceDeclarationRules}
            />
            <DangerAlertWhenErrorCode errorCode={errorCode} />
            <Loading
                item={insurerServiceDeclarationRules}
                loaded={(serviceInsurers) => (
                    <>
                        <InsurerServiceDeclarationRulesListTable
                            items={serviceInsurers}
                            onRemove={handleRemove}
                            page={page}
                            serviceId={serviceId}
                        />
                        {total > pageSize && (
                            <Pagination
                                isDisabled={isSearching}
                                onChange={handlePaginationChange}
                                page={page}
                                pageSize={pageSize}
                                totalItems={total}
                            />
                        )}
                    </>
                )}
            />
        </>
    );

    async function search(pageNumber: number, pageSizeNumber: number = pageSize) {
        const url = `/api/insurerServiceDeclarationRules?serviceId=${serviceId}&includeItemAuthorizations=canUpdate,canDelete&includeListAuthorizations=canAdd&page=${pageNumber}&limit=${pageSizeNumber}`;
        setIsSearching(true);
        const result = await abortableFetch.fetchJson(url, 'GET');
        setCanAdd(result.json.authorization.canAdd);
        setIsSearching(false);
        setInsurerServiceDeclarationRules(result.json.items);
        setTotal(result.json.total);
    }

    async function handleRemove(id: string) {
        const url = `/api/insurerServiceDeclarationRules/${id}`;
        setErrorCode(undefined);
        setIsSearching(true);
        const result = await abortableFetch.fetchJson(url, 'DELETE', {}, [
            ValidationErrorCode.INSURER_SERVICE_HAS_LINKED_PRICES,
            ValidationErrorCode.INSURER_SERVICE_HAS_LINKED_DECLARATION_ACTIVITY_PERIODS,
        ]);

        setErrorCode(result.errorCode);

        await search(page);
    }
}
